<template>
  <li class="mb-1">

    <!-- Fila principal del nodo -->
    <div class="nai-row" :class="esSeleccionado ? 'nai-activo' : ''"
      @click="$emit('seleccionar', nodo)">

      <!-- Toggle hijos -->
      <span class="nai-toggle" @click.stop="expandido = !expandido">
        <i v-if="tieneHijos" :class="expandido ? 'fa fa-caret-down' : 'fa fa-caret-right'"></i>
        <i v-else class="fa fa-minus" style="font-size:.6rem; opacity:.3;"></i>
      </span>

      <!-- Icono nodo -->
      <i class="fa fa-building nai-ico-nodo mr-1"></i>

      <!-- Nombre y código -->
      <span class="nai-nombre">{{ nodo.nodo_descripcion }}</span>
      <span class="nai-codigo ml-1">[{{ nodo.nodo_codigo }}]</span>

      <!-- Badge usuarios (siempre visible, usa datos ya cargados del prop) -->
      <span class="nai-badge-usr"
        :class="{ 'nai-badge-activo': mostrarUsuarios }"
        :title="mostrarUsuarios ? 'Ocultar usuarios' : 'Ver usuarios asignados'"
        @click.stop="mostrarUsuarios = !mostrarUsuarios">
        <i class="fa fa-users" style="font-size:.65rem;"></i>
        <span v-if="funcionarios.length" class="nai-usr-count">{{ funcionarios.length }}</span>
      </span>

      <!-- Acciones inline (visibles al hover, solo admin) -->
      <div v-if="esAdmin" class="nai-actions" @click.stop>
        <button class="nai-btn" title="Agregar sub-nodo"
          @click.stop="$emit('crear-hijo', nodo)">
          <i class="fa fa-plus"></i>
        </button>
        <button class="nai-btn" title="Editar"
          @click.stop="$emit('editar', nodo)">
          <i class="fa fa-pencil"></i>
        </button>
        <button class="nai-btn nai-btn-danger" title="Eliminar"
          @click.stop="$emit('eliminar', nodo)">
          <i class="fa fa-trash"></i>
        </button>
        <button class="nai-btn nai-btn-user" title="Asignar usuario"
          @click.stop="$emit('asignar-usuario', nodo)">
          <i class="fa fa-user-plus"></i>
        </button>
      </div>
    </div>

    <!-- Lista de usuarios del nodo -->
    <ul v-if="mostrarUsuarios && funcionarios.length" class="list-unstyled nai-usr-list">
      <li v-for="usr in funcionarios" :key="usr.id" class="nai-usr-item">
        <div class="nai-usr-avatar">{{ iniciales(usr.name) }}</div>
        <div class="nai-usr-info">
          <div class="nai-usr-name">
            {{ usr.name }}
            <span v-if="userCounts[usr.id]?.total" class="nai-corr-count"
              :class="userCounts[usr.id].vencidos > 0 ? 'nai-corr-venc' : userCounts[usr.id].esta_semana > 0 ? 'nai-corr-sem' : 'nai-corr-ok'">
              {{ userCounts[usr.id].total }}
            </span>
          </div>
          <div class="nai-usr-email">{{ usr.email || usr.nom_usuario || '—' }}</div>
        </div>
      </li>
    </ul>

    <!-- Sin usuarios -->
    <div v-if="mostrarUsuarios && funcionarios.length === 0" class="nai-usr-empty">
      <i class="fa fa-user-times mr-1"></i>Sin usuarios asignados
    </div>

    <!-- Hijos recursivos (colapsados por defecto) -->
    <ul v-if="tieneHijos && expandido" class="list-unstyled nai-hijos">
      <nodo-arbol-item
        v-for="hijo in hijos"
        :key="hijo.nodo_id"
        :nodo="hijo"
        :nodo-activo-id="nodoActivoId"
        :es-admin="esAdmin"
        :user-counts="userCounts"
        @seleccionar="$emit('seleccionar', $event)"
        @crear-hijo="$emit('crear-hijo', $event)"
        @editar="$emit('editar', $event)"
        @eliminar="$emit('eliminar', $event)"
        @asignar-usuario="$emit('asignar-usuario', $event)"
      />
    </ul>

  </li>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  nodo:         { type: Object,  required: true },
  nodoActivoId: { type: Number,  default: null },
  esAdmin:      { type: Boolean, default: false },
  userCounts:   { type: Object,  default: () => ({}) },
})

defineEmits(['seleccionar', 'crear-hijo', 'editar', 'eliminar', 'asignar-usuario'])

// Árbol inicia colapsado
const expandido       = ref(false)
const mostrarUsuarios = ref(false)

const hijos = computed(() =>
  props.nodo.hijos_recursivos || props.nodo.hijosRecursivos || []
)
// Funcionarios ya vienen cargados con el árbol (with(['hijosRecursivos','funcionarios']))
const funcionarios   = computed(() => props.nodo.funcionarios || [])
const tieneHijos     = computed(() => hijos.value.length > 0)
const esSeleccionado = computed(() => props.nodo.nodo_id === props.nodoActivoId)

function iniciales(nombre) {
  return (nombre || '?').split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}
</script>

<style scoped>
/* ── Fila principal ── */
.nai-row {
  display: flex;
  align-items: center;
  padding: 4px 6px;
  border-radius: 5px;
  cursor: pointer;
  transition: background .15s;
  gap: 2px;
  position: relative;
}
.nai-row:hover        { background: #f8f0f2; }
.nai-activo           { background: var(--color-brand-primary) !important; color: #fff; border-radius: 5px; }
.nai-activo .nai-codigo { color: rgba(255,255,255,.6); }

/* Toggle expand */
.nai-toggle {
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: .8rem;
}
.nai-ico-nodo { font-size: .73rem; color: var(--color-brand-primary); flex-shrink: 0; }
.nai-activo .nai-ico-nodo { color: #ffd700; }

.nai-nombre  { font-size: .82rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nai-codigo  { font-size: .67rem; color: #888; white-space: nowrap; flex-shrink: 0; }

/* ── Badge usuarios ── */
.nai-badge-usr {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 5px;
  border-radius: 10px;
  border: 1px solid #dee2e6;
  background: #fff;
  color: var(--color-brand-primary);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;
  transition: background .15s;
  font-size: .65rem;
}
.nai-badge-usr:hover,
.nai-badge-activo     { background: #f8f0f2; border-color: #c0a0a8; }
.nai-activo .nai-badge-usr {
  background: rgba(255,255,255,.15);
  border-color: rgba(255,255,255,.4);
  color: #fff;
}
.nai-usr-count {
  font-size: .65rem;
  font-weight: 700;
  background: var(--color-brand-primary);
  color: #fff;
  border-radius: 8px;
  padding: 0 4px;
  min-width: 14px;
  text-align: center;
}
.nai-activo .nai-usr-count { background: #ffd700; color: var(--color-brand-primary); }

/* ── Botones de acción inline (aparecen en hover) ── */
.nai-actions {
  display: none;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: 2px;
}
.nai-row:hover .nai-actions,
.nai-activo .nai-actions { display: flex; }

.nai-btn {
  border: none;
  background: transparent;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: .68rem;
  color: #555;
  cursor: pointer;
  transition: background .12s, color .12s;
  line-height: 1.4;
}
.nai-btn:hover              { background: rgba(0,0,0,.1); color: var(--color-brand-primary); }
.nai-btn-danger:hover       { color: #dc3545 !important; }
.nai-btn-user:hover         { color: #28a745 !important; }
.nai-activo .nai-btn        { color: rgba(255,255,255,.7); }
.nai-activo .nai-btn:hover  { background: rgba(255,255,255,.2); color: #fff; }

/* ── Lista de usuarios ── */
.nai-usr-list {
  margin: 0 0 4px 22px;
  padding: 2px 0;
  border-left: 2px solid #f0d0d8;
}
.nai-usr-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
}
.nai-usr-avatar {
  width: 24px; height: 24px;
  border-radius: 50%;
  background: var(--color-brand-primary); color: #fff;
  font-size: .62rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.nai-usr-info  { min-width: 0; }
.nai-usr-name  { font-size: .75rem; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.nai-usr-email { font-size: .67rem; color: #888; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.nai-usr-empty {
  margin: 0 0 4px 22px;
  padding: 2px 8px;
  font-size: .7rem;
  color: #aaa;
  border-left: 2px solid #f0d0d8;
}

/* ── Badge conteo correspondencias ── */
.nai-corr-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  padding: 0 4px;
  border-radius: 8px;
  font-size: .62rem;
  font-weight: 700;
  color: #fff;
  margin-left: 4px;
  line-height: 1.5;
}
.nai-corr-venc { background: #c62828; }
.nai-corr-sem  { background: #f57c00; }
.nai-corr-ok   { background: #2e7d32; }

/* ── Hijos con sangría ── */
.nai-hijos { padding-left: 14px; margin: 0; border-left: 1px dashed #e0b0b8; margin-left: 8px; }
</style>
